import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { DefaultNewGameConceptId } from "../constants";
import {
  CrmGameConceptStatus,
  CrmGameConceptStatusOrder2,
  CrmGameConceptSubStatus,
} from "../models/crmStatus";
import { AaaGameConceptData, IdXboxGameConceptData } from "../models/forms";
import { getGameConceptsList } from "../redux/selectors";
import {
  AaaGameConceptDataSet,
  GameConceptState,
  IdXboxGameConceptDataSet,
} from "../server/crm/api/models/forms";

export const isGameConceptHardRejected = (
  gameConceptStatus: CrmGameConceptStatus | undefined,
  gameConceptSubStatus: CrmGameConceptSubStatus | undefined
) =>
  gameConceptStatus === CrmGameConceptStatus.Rejected &&
  gameConceptSubStatus !== CrmGameConceptSubStatus.RejectedSoftNeedsDevToken &&
  gameConceptSubStatus !== CrmGameConceptSubStatus.RejectedSoftAlreadyHaveDevToken &&
  gameConceptSubStatus !== CrmGameConceptSubStatus.RejectedSoftGeneral;

export const isGameConceptSoftRejected = (
  gameConceptStatus: CrmGameConceptStatus | undefined,
  gameConceptSubStatus: CrmGameConceptSubStatus | undefined
) =>
  gameConceptStatus === CrmGameConceptStatus.Rejected &&
  (gameConceptSubStatus === CrmGameConceptSubStatus.RejectedSoftNeedsDevToken ||
    gameConceptSubStatus === CrmGameConceptSubStatus.RejectedSoftAlreadyHaveDevToken ||
    gameConceptSubStatus === CrmGameConceptSubStatus.RejectedSoftGeneral);

export const pickGameConceptForDashboard = (
  gameConcepts: IdXboxGameConceptDataSet | AaaGameConceptDataSet,
  selectedGameConceptId: string | undefined
): ((IdXboxGameConceptData | AaaGameConceptData) & GameConceptState) | undefined => {
  // If a new application were forced to happen by calling /publish/id?game-concept-id=new
  // from the Welcome Partner page, then at this point we just return an empty game concept.
  if (!selectedGameConceptId || selectedGameConceptId === DefaultNewGameConceptId) {
    return undefined;
  }

  return gameConcepts[selectedGameConceptId] ?? undefined;
};

export type GameConceptApplication = {
  id: string;
  gameConceptInfo: (IdXboxGameConceptData | AaaGameConceptData) & GameConceptState;
};

export type CategorizedGameConcepts = {
  incompleteApplications: GameConceptApplication[];
  incompleteReadOnlyApplications: GameConceptApplication[];
  completeApplications: GameConceptApplication[];
  rejectedApplications: GameConceptApplication[];
};

export const useCategorizedGameConcepts = (): CategorizedGameConcepts | undefined => {
  const crmGameConceptsList = useSelector(getGameConceptsList);
  const [applications, setApplications] = useState<CategorizedGameConcepts | undefined>(undefined);

  useEffect(() => {
    const incomplete = [];
    const incompleteReadOnly = [];
    const complete = [];
    const rejected = [];

    for (const key in crmGameConceptsList) {
      const gameConcept = crmGameConceptsList[key];
      const applicationToStore = { id: key, gameConceptInfo: gameConcept };
      switch (gameConcept.state.status) {
        case CrmGameConceptStatus.Accepted:
          complete.push(applicationToStore);
          break;
        case CrmGameConceptStatus.Rejected:
          rejected.push(applicationToStore);
          break;
        case CrmGameConceptStatus.UnderReview:
        case CrmGameConceptStatus.AdditionalContractsSent:
          incompleteReadOnly.push(applicationToStore);
          break;
        case CrmGameConceptStatus.Unset:
        case CrmGameConceptStatus.MoreInfoNeeded:
          incomplete.push(applicationToStore);
          break;
      }
    }
    setApplications({
      incompleteApplications: incomplete,
      incompleteReadOnlyApplications: incompleteReadOnly,
      completeApplications: complete,
      rejectedApplications: rejected,
    });
  }, [crmGameConceptsList]);

  return applications;
};

export const usePaginatedGameConcepts = (): GameConceptApplication[] | undefined => {
  const crmGameConceptsList = useSelector(getGameConceptsList);
  const [applications, setApplications] = useState<GameConceptApplication[] | undefined>(undefined);

  useEffect(() => {
    const concepts = [];

    for (const key in crmGameConceptsList) {
      const gameConcept = crmGameConceptsList[key];
      const applicationToStore = { id: key, gameConceptInfo: gameConcept };
      concepts.push(applicationToStore);
    }
    setApplications(concepts);
  }, [crmGameConceptsList]);

  return applications;
};

export enum GameConceptFilter {
  Draft = "Draft",
  UnderReview = "UnderReview",
  Approved = "Approved",
  Rejected = "Rejected",
  NeedMoreInfo = "NeedMoreInfo",
}

export const getMostAdvancedGameConcept = (
  gameConceptsList: IdXboxGameConceptDataSet
): (IdXboxGameConceptData & GameConceptState) | undefined => {
  let highestStatusNumericValue = -1;
  let mostAdvancedGameConcept: (IdXboxGameConceptData & GameConceptState) | undefined;

  for (const key in gameConceptsList) {
    const gameConcept = gameConceptsList[key];
    const gameConceptStatus = gameConcept.state.status;
    const statusNumericValue = CrmGameConceptStatusOrder2[gameConceptStatus];

    if (statusNumericValue > highestStatusNumericValue) {
      highestStatusNumericValue = CrmGameConceptStatusOrder2[gameConceptStatus];
      mostAdvancedGameConcept = gameConcept;
    }
  }

  return mostAdvancedGameConcept;
};
